import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import type { ReductionReason } from '@/utils/shareBalanceChange';

interface ReductionReasonFieldProps {
  /** How many shares the holding is going down by. Positive. */
  reducingBy: number;
  value?: ReductionReason;
  onChange: (reason: ReductionReason) => void;
}

/**
 * Asked whenever a holding goes down, because the arithmetic cannot tell the two
 * cases apart and the register has to.
 *
 * Cancelling retires shares — a corporate act that happened, and that the
 * statutory register must show as having happened. A correction says the balance
 * was recorded wrongly and never should have read that way. Defaulting to either
 * one puts a claim in the register that nobody made, so there is no default.
 */
export const ReductionReasonField = ({
  reducingBy,
  value,
  onChange,
}: ReductionReasonFieldProps) => (
  <div className="space-y-3 rounded-md border border-border bg-muted/40 p-3">
    <div className="space-y-1">
      <Label className="text-sm">
        This reduces the holding by {reducingBy.toLocaleString()} shares. What happened?
      </Label>
      <p className="text-xs text-muted-foreground">
        The register records these differently, so this cannot be assumed.
      </p>
    </div>

    <RadioGroup value={value ?? ''} onValueChange={(next) => onChange(next as ReductionReason)}>
      <div className="flex items-start gap-2">
        <RadioGroupItem value="CANCELLATION" id="reduction-cancellation" className="mt-0.5" />
        <Label htmlFor="reduction-cancellation" className="font-normal leading-snug">
          Cancel the shares
          <span className="block text-xs text-muted-foreground">
            They were bought back, forfeited or surrendered. Recorded as a cancellation.
          </span>
        </Label>
      </div>

      <div className="flex items-start gap-2">
        <RadioGroupItem value="CORRECTION" id="reduction-correction" className="mt-0.5" />
        <Label htmlFor="reduction-correction" className="font-normal leading-snug">
          Correct the balance
          <span className="block text-xs text-muted-foreground">
            The holding was recorded wrongly. Recorded as an adjustment.
          </span>
        </Label>
      </div>
    </RadioGroup>
  </div>
);
